import axios from "axios";
import config from "./config";

// Communities we've already injected for, so section changes / re-renders don't
// duplicate the creator's pixels within a single page load.
const injected = new Set();

// Inject one parsed node into the DOM slot named by the blob key. Scripts are
// re-created so the browser executes them (moving a parsed <script> node does
// not run it); pixels/noscript/other markup are appended as-is.
// Which source the current injectSlot() pass belongs to: "project" (Advanced /
// General, via /customscript) or "course" (a course's own code).
let currentSource = "project";

/*
 * Every node we put in the DOM, by source.
 *
 * Held by REFERENCE rather than found again with a selector, because a slot's
 * content is not always an element: a creator who types prose (or anything not
 * wrapped in a tag) produces a TEXT node, and a text node cannot carry
 * data-script-source and is invisible to querySelectorAll. Those nodes were
 * therefore injected and never removed — they survived leaving the course and
 * were added again on returning, which is the "it prints twice" report.
 */
const injectedNodes = { project: [], course: [] };

/*
 * What the injected script itself put on the page.
 *
 * Removing our <script> tag does not undo what it DID. A chat-widget embed, for
 * instance, appends its own div and loader straight to document.body — so
 * leaving the course took our script node away and left the widget sitting on
 * the course list. Nothing else tracks those nodes, so we watch for them.
 *
 * Scope is deliberately narrow: DIRECT children of <head> and <body> only, no
 * subtree. Everything React renders lives inside its root container, so this
 * sees third-party additions without drowning in the app's own re-renders.
 */
let watcher = null;

/** Nodes React owns carry an internal __react* key — never remove those. */
function isReactOwned(node) {
  try {
    return Object.keys(node).some((k) => k.startsWith("__react"));
  } catch (e) {
    return false;
  }
}

function startWatching(source) {
  stopWatching();
  if (typeof MutationObserver === "undefined") return;

  watcher = new MutationObserver((records) => {
    records.forEach((rec) => {
      Array.from(rec.addedNodes).forEach((node) => {
        if (node.nodeType !== 1) return;
        // A React portal mounts its container on <body>; taking it out from
        // under React would break the unmount it is about to perform itself.
        if (isReactOwned(node)) return;
        if (injectedNodes[source].includes(node)) return;
        injectedNodes[source].push(node);
      });
    });
  });

  watcher.observe(document.head, { childList: true });
  watcher.observe(document.body, { childList: true });
}

function stopWatching() {
  if (watcher) {
    watcher.disconnect();
    watcher = null;
  }
}

function place(node, slot) {
  let el = node;
  if (node.nodeType === 1 && node.tagName === "SCRIPT") {
    el = document.createElement("script");
    Array.from(node.attributes).forEach((a) => el.setAttribute(a.name, a.value));
    el.text = node.textContent || "";
  }
  if (el.setAttribute) {
    el.setAttribute?.("data-custom-script", "1");
    // Which source put it here. A course override removes the project's nodes
    // and leaves its own, so the two must be tellable apart in the DOM.
    el.setAttribute?.("data-script-source", currentSource);
  }

  if (slot === "head") document.head.appendChild(el);
  else if (slot === "bodyend") document.body.appendChild(el);
  else document.body.insertBefore(el, document.body.firstChild); // bodystart

  injectedNodes[currentSource].push(el);
}

// Parse an HTML string and inject every top-level node into `slot`.
function injectSlot(html, slot) {
  if (!html || String(html).trim() === "") return;
  const holder = document.createElement("div");
  holder.innerHTML = html;
  // Snapshot first — placing a node removes it from `holder.childNodes`.
  Array.from(holder.childNodes)
    /*
     * ELEMENTS ONLY.
     *
     * A retargeting slot holds scripts and pixels — <script>, <img>, <noscript>,
     * an iframe. Anything that parses to a bare TEXT node cannot execute; the
     * browser can only paint it. So a creator who pastes prose (or JS without
     * its <script> wrapper) used to get that text rendered at the top of every
     * lesson page, which is how a placeholder hint ended up visible on a live
     * course. Dropping it is strictly better than showing it: the content was
     * never going to run either way, and now it does not deface the page.
     *
     * Comment nodes are skipped for the same reason — nothing to run.
     */
    .filter((node) => node.nodeType === 1)
    .forEach((node) => place(node, slot));
}

/**
 * Fetch the creator's Advanced → Custom Script for this community and inject it.
 *
 * The API returns `custom_script` as the legacy JSON blob the admin saves:
 *   { head, bodystart, bodyend, status }
 * Each slot is raw HTML (analytics, retargeting pixels, GTM, chat widgets) that
 * goes into a specific place — <head>, top of <body>, end of <body> — matching
 * the legacy learner app. `status === 0` is the creator's kill-switch (Settings
 * → Advanced) and skips injection entirely.
 *
 * Runs at most once per community per load. Failures are swallowed so a tracking
 * snippet can never break the learner app; the guard clears on error to retry.
 */
export async function injectCustomScript(funnelId) {
  if (!funnelId || injected.has(funnelId)) return;
  injected.add(funnelId);

  let raw = "";
  try {
    const res = await axios.post(`${config.API_URL}/customscript`, { funnel_id: funnelId });
    raw = res.data?.custom_script || "";
  } catch (e) {
    injected.delete(funnelId); // allow a retry on the next load
    return;
  }

  // Sentinel the API returns when nothing is configured.
  if (!raw || raw.trim() === "" || raw.trim() === "No custom script found") return;

  let blob;
  try {
    blob = JSON.parse(raw);
  } catch (e) {
    return; // not the expected blob — never inject unparsed markup blindly
  }
  if (!blob || typeof blob !== "object") return;

  // Kill-switch: an explicit status of 0 disables all injection.
  if (blob.status !== undefined && Number(blob.status) === 0) return;

  // Remember it, so leaving a course can put the project script back without
  // re-fetching, and so a project script that arrives DURING an override is not
  // simply lost.
  projectBlobs.set(funnelId, blob);

  /*
   * Defer while a course override is active.
   *
   * This call is fired from MembershipHome/login when the community fetch
   * resolves, which on a deep link into a lesson can land AFTER the course
   * override has already run. Injecting here would put the project script back
   * on top of the course's — both would run, which is exactly what the override
   * exists to prevent. reinjectProject() applies it when the override lifts.
   */
  if (activeCourse !== null || awaitingCourse || onCoursePage()) {
    // Held. Whichever way the course decision goes, it releases this:
    // an override keeps it held, no override calls releaseProject().
    awaitingCourse = true;
    return;
  }

  injectProjectBlob(blob);
}

/** Inject one project blob, tagged as the project source. */
function injectProjectBlob(blob) {
  currentSource = "project";
  injectSlot(blob.head, "head");
  injectSlot(blob.bodystart, "bodystart");
  injectSlot(blob.bodyend, "bodyend");
}


/* ────────────────────────────────────────────────────────────────────────────
 * Per-course retargeting code — the highest-priority source.
 *
 * Priority, top wins and wins ALONE:
 *   1. the course's own code      (this file's injectCourseScript)
 *   2. Customer Advanced Script   ) both resolved server-side into the one
 *   3. General Retargeting Code   ) blob /customscript returns
 *
 * The learner app is a single page: the project script is injected once at
 * login/home and then stays in the DOM across navigation. So "the course
 * overrides the project" cannot just mean "inject the course code too" — the
 * project's nodes have to come OUT while a course that has its own code is open,
 * and go back IN when the learner leaves it. Both directions are handled here,
 * which is why this lives beside injectCustomScript rather than in the view.
 * ──────────────────────────────────────────────────────────────────────────── */

// The course whose code is currently injected, or null when the project's is.
let activeCourse = null;

// The course currently being APPLIED. Needed because the guard below runs
// before an await: React StrictMode double-invokes effects in dev, and the
// lesson view's effect also re-runs once member.funnel_id resolves — so two
// calls for the same course can both pass an `activeCourse` check while it is
// still null, both fetch, and both inject. That is a doubled pixel.
// NONE, not null: null is a VALID token meaning "leaving the course", so using
// it as the idle sentinel made the guard below swallow every leave call and the
// course script never came back out.
const NONE = Symbol("no pending course");
let pending = NONE;

/*
 * Is a course decision still owed to us?
 *
 * THE POINT OF THIS FLAG: a <script> that has already been injected has already
 * RUN. Removing its node afterwards does not un-fire the pixel — the request is
 * gone. So "the course overrides the project" cannot be implemented by injecting
 * the project script and taking it back out; the project script must never run
 * in the first place on a course that has its own code.
 *
 * On a deep link into a lesson, /customscript (fired by MembershipHome) and
 * /course-retargeting (fired by the lesson view) are in flight together, and
 * either can win. So project injection is HELD from the moment we know a course
 * is involved until the course decision lands.
 */
let awaitingCourse = false;

/** Does the current URL address a course? Used as the initial hold signal. */
function onCoursePage() {
  try {
    return Boolean(new URLSearchParams(window.location.search).get("course"));
  } catch (e) {
    return false;
  }
}

/**
 * Called SYNCHRONOUSLY by the lesson view before it fetches, so the hold is in
 * place no matter which request resolves first.
 */
export function claimCourseScript(courseToken) {
  if (courseToken) awaitingCourse = true;
}

/**
 * Release the hold and apply whatever project script was deferred.
 *
 * Safe to call more than once: reinjectProject removes existing project nodes
 * before adding, so a double release cannot double the script.
 */
function releaseProject() {
  awaitingCourse = false;
  reinjectProject();
}

// funnelId -> the project blob /customscript returned, so restoring after an
// override costs no request and cannot race a second fetch.
const projectBlobs = new Map();

function removeBySource(source) {
  if (source === "course") stopWatching();

  injectedNodes[source].forEach((el) => {
    if (typeof el.remove === "function") el.remove();
    else el.parentNode?.removeChild(el);
  });
  injectedNodes[source] = [];
}

/**
 * Apply the right script for the course the learner is looking at.
 *
 * Call it with the course on every lesson/course view, and with a null course
 * when leaving the course area. Safe to call repeatedly: it no-ops when the
 * course has not changed.
 *
 * Failures are swallowed. A tracking snippet must never break the lesson page,
 * and the project script staying put is the correct fallback.
 */
export async function injectCourseScript({ courseToken, funnelId } = {}) {
  const token = courseToken ? String(courseToken) : null;

  // Already applied, or already being applied. `pending` is what makes this
  // safe against concurrent calls — see its declaration.
  if (token === activeCourse || token === pending) return;

  pending = token;

  let blob = null;
  if (token) {
    try {
      const res = await axios.post(`${config.MEMBERSHIP_API_URL}/course-retargeting`, {
        course_id: parseInt(token, 10),
        funnel_id: funnelId,
      });
      const raw = res.data?.custom_script;
      // null is the normal answer for a course with no code of its own, and for
      // one whose code is switched off — both mean "let the project script run".
      blob = raw ? JSON.parse(raw) : null;
    } catch (e) {
      if (pending === token) pending = NONE;
      return; // leave whatever is already injected alone
    }
  }

  // A newer call overtook this one while it was in flight — that call owns the
  // DOM now, so this stale answer must not be applied on top of it.
  if (pending !== token) return;
  pending = NONE;

  // Nothing course-specific to run. Two cases, one action: the learner left a
  // course that had code, OR this course has none and the project script has
  // been held waiting to hear that. Both mean "the project script runs here".
  if (!blob) {
    removeBySource("course");
    activeCourse = null;
    releaseProject();
    return;
  }

  removeBySource("course");
  removeBySource("project");
  activeCourse = token;
  awaitingCourse = false; // decided: the course wins, the project stays out

  currentSource = "course";
  // Watch from BEFORE the script runs, and keep watching: an embed typically
  // appends on window load or after fetching its own loader, long after this
  // function has returned.
  startWatching("course");
  try {
    injectSlot(blob.head, "head");
    injectSlot(blob.bodystart, "bodystart");
    injectSlot(blob.bodyend, "bodyend");
  } finally {
    currentSource = "project";
  }
}

/**
 * Put the project script back after a course override is lifted.
 *
 * Replays the CACHED blobs rather than calling injectCustomScript again. The
 * earlier version cleared the `injected` guard and re-fetched, which raced a
 * still-in-flight first fetch and could inject the same project script twice.
 * Removing the project nodes first makes this idempotent however it is reached.
 */
function reinjectProject() {
  removeBySource("project");
  projectBlobs.forEach((blob) => injectProjectBlob(blob));
}
